import { useState } from "react";
import { api, ApiError } from "../api/client";
import { useQuery } from "../api/useQuery";
import { Photo } from "./Photo";
import { fmtDateTime, fmtTime } from "./format";
import type { EquipmentItem, EquipmentLine, ShiftEquipment as Equipment } from "../api/types";

const KINDS: Record<string, string> = {
  transport: "Транспорт",
  radio: "Радиостанция",
  special: "Спецсредства",
  other: "Другое",
};

const CONDITIONS: Record<string, string> = {
  ok: "Исправно",
  damaged: "Неисправно",
  incomplete: "Некомплект",
  missing: "Отсутствует",
};

/**
 * Чек-лист смены: что выдано и что сдано. Запись инспектора не меняется —
 * начальник оформляет исправление с причиной, и оно показывается рядом.
 */
export function ShiftEquipment({ shiftId, onChanged }: { shiftId: string; onChanged?: () => void }) {
  const { data, error, loading, reload } = useQuery<Equipment>(`/api/v1/shifts/${shiftId}/equipment`);

  if (loading && !data) return <p className="muted small">Загрузка чек-листа…</p>;
  if (error) return <div className="error">{error}</div>;
  if (!data || data.items.length === 0) return <p className="muted small">Чек-лист имущества в смене не заполнялся.</p>;

  return (
    <div className="equipment">
      {data.items.map((it) => (
        <Item key={it.kind} item={it} canCorrect={data.can_correct}
          onCorrected={() => { reload(); onChanged?.(); }} />
      ))}
    </div>
  );
}

function Item({ item, canCorrect, onCorrected }: { item: EquipmentItem; canCorrect: boolean; onCorrected: () => void }) {
  return (
    <section className="equipment-item">
      <div className="equipment-head">
        <strong>{KINDS[item.kind] ?? item.kind}</strong>
        {item.problems.map((p) => <span key={p} className="badge danger">{p}</span>)}
        {item.warnings.map((w) => <span key={w} className="badge warn">{w}</span>)}
        {item.problems.length === 0 && item.warnings.length === 0 && <span className="badge ok">в порядке</span>}
      </div>
      <div className="equipment-phases">
        <Line title="Выдано" line={item.issue} canCorrect={canCorrect} onCorrected={onCorrected} />
        <Line title="Сдано" line={item.return} canCorrect={canCorrect} onCorrected={onCorrected} />
      </div>
    </section>
  );
}

function Line({ title, line, canCorrect, onCorrected }: {
  title: string; line: EquipmentLine | null; canCorrect: boolean; onCorrected: () => void;
}) {
  const [showPhotos, setShowPhotos] = useState(false);
  const [correcting, setCorrecting] = useState(false);

  if (!line) {
    return (
      <div className="equipment-line">
        <div className="muted small">{title}</div>
        <div className="muted">нет записи</div>
      </div>
    );
  }

  const photos = line.photos.filter((p) => p.state === "complete");
  return (
    <div className="equipment-line">
      <div className="muted small">{title} · {fmtTime(line.device_time)}</div>
      <div>
        № <Value now={line.inventory_no || "—"} was={line.original_inventory_no || "—"} />
        {" · "}
        <Value now={CONDITIONS[line.condition] ?? line.condition}
          was={CONDITIONS[line.original_condition] ?? line.original_condition} />
      </div>
      {(line.note || line.original_note) && (
        <div className="small"><Value now={line.note || "—"} was={line.original_note || "—"} /></div>
      )}

      {line.corrections.map((c, i) => (
        <div key={i} className="small correction">
          Исправил {c.author}, {fmtDateTime(c.created_at)}: {c.reason}
        </div>
      ))}

      <div className="filters" style={{ margin: "6px 0 0" }}>
        {photos.length > 0 && (
          <button className="secondary" onClick={() => setShowPhotos(!showPhotos)}>
            {showPhotos ? "Скрыть фото" : `Фото: ${photos.length}`}
          </button>
        )}
        {line.photos.length > photos.length && (
          <span className="badge warn">фото догружается: {line.photos.length - photos.length}</span>
        )}
        {canCorrect && !correcting && (
          <button className="secondary" onClick={() => setCorrecting(true)}>Исправить</button>
        )}
      </div>
      {showPhotos && <div className="photos" style={{ marginTop: 8 }}>{photos.map((p) => <Photo key={p.id} id={p.id} />)}</div>}
      {correcting && (
        <CorrectionForm line={line} onCancel={() => setCorrecting(false)}
          onDone={() => { setCorrecting(false); onCorrected(); }} />
      )}
    </div>
  );
}

/** Действующее значение; если оно исправлено, рядом зачёркнуто исходное. */
function Value({ now, was }: { now: string; was: string }) {
  if (now === was) return <>{now}</>;
  return <><s className="muted">{was}</s> {now}</>;
}

function CorrectionForm({ line, onDone, onCancel }: { line: EquipmentLine; onDone: () => void; onCancel: () => void }) {
  const [inventory, setInventory] = useState(line.inventory_no);
  const [condition, setCondition] = useState(line.condition);
  const [note, setNote] = useState(line.note);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Отправляем только то, что действительно меняется.
  const patch: Record<string, string> = {};
  if (inventory.trim() !== line.inventory_no) patch.inventory_no = inventory.trim();
  if (condition !== line.condition) patch.condition = condition;
  if (note.trim() !== line.note) patch.note = note.trim();
  const changed = Object.keys(patch).length > 0;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/equipment/${line.id}/corrections`, { reason: reason.trim(), ...patch });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить исправление");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="correction-form" onSubmit={(e) => void submit(e)}>
      {error && <div className="error" role="alert">{error}</div>}
      <div className="filters" style={{ margin: 0 }}>
        <label className="field">Инвентарный номер
          <input value={inventory} onChange={(e) => setInventory(e.target.value)} />
        </label>
        <label className="field">Состояние
          <select value={condition} onChange={(e) => setCondition(e.target.value)}>
            {Object.entries(CONDITIONS).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        </label>
      </div>
      <label className="field">Замечание
        <input value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <label className="field">Причина исправления
        <input required value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Например: номер записан с ошибкой, сверено с журналом выдачи" />
      </label>
      <div className="filters" style={{ margin: 0 }}>
        <button type="submit" disabled={busy || !changed || !reason.trim()}>Сохранить исправление</button>
        <button type="button" className="secondary" onClick={onCancel}>Отмена</button>
      </div>
      <p className="muted small" style={{ margin: 0 }}>Запись инспектора сохранится как есть; исправление будет видно рядом с автором и причиной.</p>
    </form>
  );
}
